import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate } from '@pk/shared';
import type { Id } from '@pk/shared';
import { useCities, useDeliveries, useMarket, useWorld } from '../../api/hooks';
import { Btn, ErrorBox, Sheet } from '../../components/ui';
import { IconChevron, ResourceIcon } from '../../components/icons';
import { Meta, Row, SheetLayout, Skeleton } from './shared';
import { OrderForm } from './MarketsView';

export function CaravansView({ actor, gold, onBrowse }: { actor: Id; gold: number; onBrowse: () => void }) {
  const { t } = useTranslation();
  const deliveries = useDeliveries();
  const cities = useCities();
  const world = useWorld();
  const [creating, setCreating] = useState(false);
  if (deliveries.isLoading) return <Skeleton rows={3} />;
  if (deliveries.isError || !deliveries.data) return <ErrorBox error={deliveries.error} onRetry={() => deliveries.refetch()} />;
  const rows = [...deliveries.data.deliveries].sort((a, b) => a.arriveDay - b.arriveDay);
  const today = world.data?.day;
  const roadDays = new Map((cities.data?.cities ?? []).map((c) => [c.cityId, c.roadDays] as const));

  return (
    <section className="tr-section">
      <div className="tr-section-head">
        <p className="tr-help">{t('eco.caravans.help')}</p>
        <Btn variant="primary" block onClick={() => setCreating(true)}>{t('eco.caravans.new')}</Btn>
      </div>
      {rows.length === 0 ? (
        <div className="tr-empty">
          <ResourceIcon k="horses" size={40} />
          <strong>{t('trade.caravans.none')}</strong>
          <span>{t('eco.caravans.emptyHelp')}</span>
          <Btn onClick={onBrowse}>{t('eco.hint.browse')}</Btn>
        </div>
      ) : (
        <div className="tr-list">
          {rows.map((d) => {
            const left = today != null ? Math.max(0, d.arriveDay - today) : null;
            const total = Math.max(left ?? 1, roadDays.get(d.fromCityId) ?? left ?? 1, 1);
            const progress = left == null ? 0 : Math.max(0.06, Math.min(1, 1 - left / total));
            return (
              <article key={d.id} className="card tr-caravan">
                <div className="tr-caravan-top">
                  <span className="tr-row-lead" aria-hidden="true"><ResourceIcon k={d.resource} /></span>
                  <div className="tr-row-main">
                    <span className="tr-row-title">{d.quantity} {t(`resource.${d.resource}`)}</span>
                    <span className="tr-row-meta"><Meta items={[<span key="r" className="tr-route">{d.fromCityName} <span aria-hidden="true">→</span> {t('eco.caravans.home')}</span>]} /></span>
                  </div>
                  <span className={`tag ${left != null && left <= 1 ? 'green' : 'blue'}`}>{left != null && left <= 1 ? t('eco.caravans.arriving') : t('eco.caravans.inTransit')}</span>
                </div>
                <span className="bar tr-route-bar" role="progressbar" aria-valuenow={Math.round(progress * 100)} aria-valuemax={100}>
                  <span className="bar-fill gold" style={{ width: `${progress * 100}%` }} />
                </span>
                <div className="tr-caravan-eta">
                  <strong>{left == null ? formatDate(d.arriveDay) : left === 0 ? t('eco.caravans.today') : t('eco.caravans.in', { count: left })}</strong>
                  <span className="tr-faint">{formatDate(d.arriveDay)}</span>
                </div>
              </article>
            );
          })}
        </div>
      )}
      {creating && <NewCaravanSheet actor={actor} gold={gold} onClose={() => setCreating(false)} />}
    </section>
  );
}

/** Two steps: pick the city to buy from, then goods + quantity (a buy order; the goods come home by caravan). */
function NewCaravanSheet({ actor, gold, onClose }: { actor: Id; gold: number; onClose: () => void }) {
  const { t } = useTranslation();
  const cities = useCities();
  const [cityId, setCityId] = useState<Id | null>(null);
  return (
    <Sheet open onClose={onClose} fixed title={t('eco.caravans.newTitle')}>
      {cityId
        ? <StepGoods cityId={cityId} actor={actor} gold={gold} onBack={() => setCityId(null)} onDone={onClose} />
        : (
          <SheetLayout>
            <p className="tr-step-label">{t('eco.caravans.step', { n: 1 })} · {t('eco.caravans.pickCity')}</p>
            {cities.isLoading ? <Skeleton rows={3} /> : !cities.data ? <ErrorBox error={cities.error} onRetry={() => cities.refetch()} /> : (
              <div className="tr-list">
                {[...cities.data.cities].sort((a, b) => (a.roadDays ?? 99) - (b.roadDays ?? 99)).map((c) => (
                  <Row key={c.cityId} onClick={() => setCityId(c.cityId)} title={c.name}
                    meta={<Meta items={[c.roadDays != null ? t('eco.caravans.arrivesIn', { count: Math.max(1, c.roadDays) }) : t('eco.noRoad'), t('eco.tariffPct', { pct: Math.round(c.tariffRate * 100) })]} />}
                    trail={<IconChevron className="tr-chev" />} />
                ))}
              </div>
            )}
          </SheetLayout>
        )}
    </Sheet>
  );
}

function StepGoods({ cityId, actor, gold, onBack, onDone }: { cityId: Id; actor: Id; gold: number; onBack: () => void; onDone: () => void }) {
  const { t } = useTranslation();
  const market = useMarket(cityId);
  if (market.isLoading) return <SheetLayout><Skeleton rows={3} /></SheetLayout>;
  if (!market.data) return <SheetLayout><ErrorBox error={market.error} onRetry={() => market.refetch()} /></SheetLayout>;
  return (
    <OrderForm market={market.data} actor={actor} gold={gold} initial={{ side: 'buy', resource: 'food' }} lockSide="buy"
      backLabel={t('eco.caravans.changeCity')} onBack={onBack} onDone={onDone} />
  );
}
